import type { Components } from "react-markdown";

import { Box, Link } from "@mui/material";
import ReactMarkdown from "react-markdown";
import rehypeSanitize from "rehype-sanitize";
import remarkGfm from "remark-gfm";

import { BodyText, SubtitleText } from "~/components/Typographies";
import resolveFernieFontFamily from "~/helpers/aiChat/resolveFernieFontFamily";

interface FernieMarkdownProps {
	content: string;
}

const components: Components = {
	a: ({ children, href }) => (
		<Link href={href} rel="noopener noreferrer" target="_blank" underline="hover">
			{children}
		</Link>
	),
	blockquote: ({ children }) => (
		<Box
			component="blockquote"
			sx={{
				borderLeft: "3px solid",
				borderLeftColor: "divider",
				color: "text.secondary",
				m: 0,
				my: 1,
				pl: 1.5
			}}
		>
			{children}
		</Box>
	),
	code: ({ children, className }) => {
		const isInline = !className?.includes("language-");
		if (isInline) {
			return (
				<Box
					component="code"
					sx={{
						backgroundColor: "action.hover",
						borderRadius: "4px",
						fontFamily: resolveFernieFontFamily("code"),
						fontSize: "0.78rem",
						px: 0.5,
						py: 0.1
					}}
				>
					{children}
				</Box>
			);
		}
		return (
			<Box
				component="code"
				sx={{ display: "block", fontFamily: resolveFernieFontFamily("code"), fontSize: "0.78rem", whiteSpace: "pre" }}
			>
				{children}
			</Box>
		);
	},
	em: ({ children }) => <em>{children}</em>,
	h1: ({ children }) => (
		<SubtitleText bold sx={{ fontSize: "1rem", fontWeight: 700, mb: 0.5, mt: 1.5 }}>
			{children}
		</SubtitleText>
	),
	h2: ({ children }) => (
		<SubtitleText bold sx={{ fontSize: "0.92rem", fontWeight: 700, mb: 0.5, mt: 1.5 }}>
			{children}
		</SubtitleText>
	),
	h3: ({ children }) => (
		<SubtitleText bold sx={{ fontSize: "0.86rem", mb: 0.5, mt: 1.25 }}>
			{children}
		</SubtitleText>
	),
	img: ({ alt }) => <span>{alt}</span>,
	li: ({ children }) => (
		<Box component="li" sx={{ "&::marker": { color: "text.secondary" }, mb: 0.25 }}>
			{children}
		</Box>
	),
	ol: ({ children }) => (
		<Box component="ol" sx={{ my: 0.5, pl: 2.5 }}>
			{children}
		</Box>
	),
	p: ({ children }) => (
		<BodyText
			sx={{
				"&:not(:last-child)": { mb: 1 },
				fontFamily: resolveFernieFontFamily("prose"),
				fontSize: "0.875rem",
				lineHeight: 1.5
			}}
		>
			{children}
		</BodyText>
	),
	pre: ({ children }) => (
		<Box
			component="pre"
			sx={{
				backgroundColor: "action.hover",
				borderRadius: "8px",
				my: 1,
				overflowX: "auto",
				p: 1.25
			}}
		>
			{children}
		</Box>
	),
	table: ({ children }) => (
		<Box sx={{ border: "1px solid", borderColor: "divider", borderRadius: "10px", my: 1.5, overflowX: "auto" }}>
			<Box
				component="table"
				sx={{
					"& th, & td": {
						borderBottom: "1px solid",
						borderBottomColor: "divider",
						px: 1.5,
						py: 1,
						textAlign: "left",
						whiteSpace: "nowrap"
					},
					"& th": { backgroundColor: "action.hover", fontWeight: 600 },
					"& tr:last-child td": { borderBottom: 0 },
					borderCollapse: "collapse",
					fontSize: "0.78rem",
					minWidth: "100%"
				}}
			>
				{children}
			</Box>
		</Box>
	),
	strong: ({ children }) => <strong>{children}</strong>,
	ul: ({ children }) => (
		<Box component="ul" sx={{ my: 0.5, pl: 2.5 }}>
			{children}
		</Box>
	)
};

const FernieMarkdown = ({ content }: FernieMarkdownProps) => (
	<Box
		sx={{
			"& > :first-of-type": { mt: 0 },
			"& > :last-child": { mb: 0 },
			color: "text.primary",
			minWidth: 0,
			overflowWrap: "anywhere"
		}}
	>
		<ReactMarkdown components={components} rehypePlugins={[rehypeSanitize]} remarkPlugins={[remarkGfm]}>
			{content}
		</ReactMarkdown>
	</Box>
);

export default FernieMarkdown;
